import { useEffect, useState, useCallback } from 'react';
import { getSubscription, createSubscription } from '@genfun/core/api/auth';
import { openTabWithUrl } from '@genfun/core/utils/openTabWithUrl';
import { BILLING, PLANS, fmtPrice, planByTier } from '../data/pricing';
import { useSession } from '../utils/session';
import { Modal } from '../components/Modal';
import { Icon } from '../components/Icon';
import { PlanPicker } from './PlanPicker';
import './AccountPopup.css';

const STATUS_META = {
    active: { label: 'Active', tone: 'success' },
    cancelled: { label: 'Cancelled', tone: 'warning' },
    past_due: { label: 'Past due', tone: 'danger' },
};

function formatDate(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// The backend doesn't return a next-billing date. It is derived as last_rebill_at (or started_at)
// + one billing cycle (SUB_CYCLE_DAYS on the backend) and shown as an estimate.
function computeNextBilling(sub) {
    const anchor = sub?.last_rebill_at || sub?.started_at;
    if (!anchor) return null;
    const d = new Date(anchor);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + BILLING.days);
    return d;
}

const daysUntil = (date) => Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

function Row({ label, accent, children }) {
    return (
        <div className={`acctRow${accent ? ' acctRow--accent' : ''}`}>
            <span className="acctRowLabel">{label}</span>
            <span className="acctRowVal">{children}</span>
        </div>
    );
}

export function AccountPopup({ isOpen, onClose }) {
    const { token, user } = useSession();
    const [sub, setSub] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [subscribingTier, setSubscribingTier] = useState(null);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!token) { setSub(null); setStatus(null); return; }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await getSubscription(token);
            setSub(data);
            setStatus(code);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!isOpen) return;
        refresh();
    }, [isOpen, refresh]);

    const handleSubscribe = (tier) => {
        if (!token || subscribingTier !== null) return;
        setSubscribingTier(tier);
        setError(null);
        openTabWithUrl(() => createSubscription(token, tier).then((r) => r.url))
            .catch((e) => setError(e.message || String(e)))
            .finally(() => setSubscribingTier(null));
    };

    const hasSub = status === 200 && sub;
    const isActive = hasSub && sub.status === 'active';
    const isCancelled = hasSub && sub.status === 'cancelled';
    const plan = hasSub ? planByTier(sub.tier) : null;
    const planName = plan?.name || (hasSub ? `Tier ${sub.tier}` : '');
    const statusMeta = hasSub ? (STATUS_META[sub.status] || { label: sub.status, tone: 'warning' }) : null;
    const nextBilling = hasSub && !isCancelled ? computeNextBilling(sub) : null;
    const accessUntil = isCancelled ? computeNextBilling(sub) : null;
    const nextIn = nextBilling ? daysUntil(nextBilling) : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} label="My account" mobile="card" className="acctModal">
            <div className="acctHead">
                <span className="acctHeadAvatar">{(user?.email || '?').slice(0, 1).toUpperCase()}</span>
                <div>
                    <div className="acctHeadName">My account</div>
                    <div className="acctHeadEmail">{user?.email || '—'}</div>
                </div>
            </div>

            {hasSub && (
                <div className="acctCredits">
                    <div className="eyebrow">Credits available</div>
                    <div className="acctCreditsValue">
                        <span className="acctCreditsNum">{user?.credits ?? 0}</span> credits
                    </div>
                    <div className="acctCreditsHint">
                        {isActive
                            ? `Refills with your ${planName} plan`
                            : accessUntil ? `Use them by ${formatDate(accessUntil)}` : 'Use them before your access ends'}
                    </div>
                </div>
            )}

            <div className="eyebrow acctSectionTitle">Subscription</div>

            {loading && !sub && <div className="skeleton acctLoading" />}

            {!loading && status === 404 && (
                <>
                    <PlanPicker busy={subscribingTier !== null} onBuy={handleSubscribe} />
                </>
            )}

            {!loading && hasSub && (
                <div className="acctSub panel">
                    <div className="acctSubTop">
                        <div>
                            <div className="acctSubPlan">{planName}</div>
                            {plan && <div className="acctSubPrice">{fmtPrice(plan.price)}<span> / {BILLING.per}</span></div>}
                        </div>
                        <span className={`acctStatus acctStatus--${statusMeta.tone}`}>
                            <span className={`dot dot--${statusMeta.tone}`} /> {statusMeta.label}
                        </span>
                    </div>

                    <div className="acctRows">
                        {sub.started_at && <Row label="Started">{formatDate(sub.started_at)}</Row>}
                        {sub.last_rebill_at && <Row label="Last payment">{formatDate(sub.last_rebill_at)}</Row>}
                        {nextBilling && (
                            <Row label="Next renewal" accent>
                                {formatDate(nextBilling)}
                                {nextIn >= 0 && <span className="acctRowMeta"> · in {nextIn} day{nextIn === 1 ? '' : 's'}</span>}
                            </Row>
                        )}
                        {accessUntil && <Row label="Access until" accent>{formatDate(accessUntil)}</Row>}
                        {sub.cancelled_at && <Row label="Cancelled on">{formatDate(sub.cancelled_at)}</Row>}
                    </div>

                    {plan && (
                        <ul className="acctFeatures">
                            {plan.features.map((f) => <li key={f} className="chip chip--static">{f}</li>)}
                        </ul>
                    )}

                    {isCancelled ? (
                        <div className="acctRenew">
                            <button type="button" className="btn btn--primary btn--block" onClick={() => handleSubscribe(sub.tier)} disabled={subscribingTier !== null}>
                                <Icon name="refresh" size={15} />
                                {subscribingTier === sub.tier ? 'Redirecting…' : `Renew ${planName}${plan ? ` — ${fmtPrice(plan.price)}${BILLING.short}` : ''}`}
                            </button>
                            <p className="acctRenewNote">
                                You keep access until {accessUntil ? formatDate(accessUntil) : 'the period ends'}.
                            </p>
                        </div>
                    ) : null /* an active plan is cancelled from the Payment rules page only (same as Pornoclaw) */}
                </div>
            )}

            {!loading && isCancelled && (
                <>
                    <div className="loginDivider acctDivider"><span>or switch plan</span></div>
                    <PlanPicker
                        plans={PLANS.filter((p) => p.tier !== sub.tier)}
                        busy={subscribingTier !== null}
                        onBuy={handleSubscribe}
                        cta={(p) => `Switch to ${p.name}`}
                    />
                </>
            )}

            {error && <div className="note note--error acctError">{error}</div>}
        </Modal>
    );
}
